import { Address4, Address6 } from 'ip-address';

export const parseMultipleValues = (values: string): string => {
	return values?.split(/\n/).join(';').split(' ').join('');
};

export const hasEmptyValues = (values: string): boolean => {
	return parseMultipleValues(values)
		?.split(';')
		.some((value) => {
			return value.length === 0;
		});
};

export const hasWhiteSpaces = (values: string): boolean => {
	return values.split(';').some((value) => {
		return value.includes(' ');
	});
};

/**
 * Verifies if an IPv4 belongs a RFC1918 (private).
 * The IA says this two can also represent privates IPs:
 *  - 172.16.0.0/12
 *  - 192.168.0.0/16
 */
export const isPrivateAddress = (ipAddress: Address4): boolean => {
	const privateSubnets = [
		'10.0.0.0/8', // Class A
		// '172.16.0.0/12', // Class B
		// '192.168.0.0/16', // Class C
	];
	return privateSubnets.some((subnet) => ipAddress.isInSubnet(new Address4(subnet)));
};

/**
 * Validates an input IPv4:
 * - validates if has a mask
 */
export const hasValidCidr = (input: string): boolean => {
	const slashIdx = input.indexOf('/');
	if (slashIdx === -1) return false;
	const prefixStr = input.slice(slashIdx + 1);
	const prefix = Number(prefixStr);
	return Number.isInteger(prefix) && prefix >= 0 && prefix <= 32;
};

/**
 * Validates an input IPv4:
 * - Accepts range "start-end" and verifies start < end in en hex (or big int)
 * - Accepts unique IP (IPv4) and verifies validness
 * - If onlyPrivate = true, the IP(s) must be private (RFC1918)
 * - If validateCidr = true, the IP(s) must have a mask
 */
export const isIpAddressInputValid = (input: string, onlyPrivate: boolean, validateCidr: boolean): boolean => {
	try {
		if (input.includes('-')) {
			const parts = input.split('-');
			if (parts.length !== 2) {
				return false;
			}
			const [startRange, endRange] = parts;
			const startAddress = new Address4(startRange);
			const endAddress = new Address4(endRange);

			return (
				startAddress.toHex() < endAddress.toHex() &&
				(!onlyPrivate || (isPrivateAddress(startAddress) && isPrivateAddress(endAddress)))
			);
		}

		if (validateCidr && !hasValidCidr(input)) return false;

		const ip = new Address4(input);
		return ip.isCorrect();
	} catch {
		return false;
	}
};

/**
 * Indicates if an IP (v4 o v6) is inside any address spaces
 * provided (can be v4 CIDR or v6 CIDR).
 */
export const isIpInAnyAddressSpace = (ipAddress: string, validAddressSpaces: string[]): boolean => {
	return (
		!!validAddressSpaces?.length &&
		validAddressSpaces.some((addressSpace) => isIpInAddressSpace(ipAddress, addressSpace))
	);
};

/**
 * Verifies if ipAddress ∈ addressSpace.
 * Tries IPv6 first. If addressSpace6 is not the correct one, tries with IPv4.
 * It'll try IPv4 as fallback, if any conversion fails.
 */
export const isIpInAddressSpace = (ipAddress: string, addressSpace: string): boolean => {
	try {
		var ip6 = new Address6(ipAddress);
		var addressSpace6 = new Address6(addressSpace);

		if (addressSpace6.isCorrect()) {
			return ip6.isInSubnet(addressSpace6);
		} else {
			var ip4 = new Address4(ipAddress);
			var addressSpace4 = new Address4(addressSpace);
			return ip4.isInSubnet(addressSpace4);
		}
	} catch (ex) {
		try {
			var ip4 = new Address4(ipAddress);
			var addressSpace4 = new Address4(addressSpace);
			return ip4.isInSubnet(addressSpace4);
		} catch (ex) {
			return false;
		}
	}
};

/**
 * Validates ports:
 * - Accepts unique numbers "N"
 * - Accepts range "A-B" (A < B)
 * - Rejects comas
 * - Min configurable; max const 65535
 */
export const isPortInputValid = (value: string, minimumPort: number): boolean => {
	const ValidPort = (port: string): number => {
		if (port.includes(',')) return -1;
		const maximumPort = 65535;
		const parsedPort = parseInt(port, 10);

		if (!isNaN(parsedPort) && parsedPort >= minimumPort && parsedPort <= maximumPort) {
			return parsedPort;
		}
		return -1;
	};

	if (!value) return false;

	if (/^([0-9]+)\-([0-9]+)$/.test(value)) {
		const [startRange, endRange] = value.split('-').map((p) => ValidPort(p));
		return startRange > -1 && endRange > -1 && startRange < endRange;
	}

	return ValidPort(value) > -1;
};

export const getAmountOfHosts = (startIp: string, endIp: string) => {
	var startIP = new Address4(startIp).bigInteger();
	var endIP = new Address4(endIp).bigInteger();

	return endIP - startIP + 1;
};

export const isLargeSubnet = (ip: string) => {
	if (ip.includes('/')) return new Address4(ip).subnetMask <= 26;
	else if (ip.includes('-')) {
		var rangeIp = ip.split('-');
		var hosts = getAmountOfHosts(rangeIp[0], rangeIp[1]);
		return hosts >= 62;
	} else return false;
};

export const ipsContainsLargeSubnet = (ips: string) => {
	var IPs = ips.replace(/\n/g, ';').split(';');
	return IPs.some((ip) => isLargeSubnet(ip));
};
